import React, { useState, useEffect } from 'react';
// import { Dialog, Transition } from '@headlessui/react';
// import { XIcon } from '@heroicons/react/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useUpsertNote, useNote } from '../../hooks/useNotes';
import { useTranslation } from 'react-i18next';

const NotesModal = ({ isOpen, onClose, dayId, initialNoteText = '', menuId }) => {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState(initialNoteText);
  
  // Fetch the current note to ensure we have the latest data
  const {
    data: noteData,
    isLoading: isNoteLoading
  } = useNote(dayId, {
    enabled: isOpen && !!dayId,
    // Don't show stale data during refetch
    keepPreviousData: false
  });
  
  // Use the React Query mutation hook
  const upsertNoteMutation = useUpsertNote();

  // Reset text when modal opens with new initial text or closes
  useEffect(() => {
    if (isOpen) {
      // If we have note data from the query, use that; otherwise use initialNoteText
      if (noteData) {
        setNoteText(noteData.text || '');
      } else {
        setNoteText(initialNoteText || ''); // Ensure it's empty string if initial is null/undefined
      }
    } else {
       // Reset state completely when closing
       setTimeout(() => { // Delay reset slightly to allow fade-out animation
          setNoteText('');
       }, 300);
    }
  }, [isOpen, initialNoteText, noteData]);

  const handleSave = async () => {
    upsertNoteMutation.mutate(
      { 
        dayId, 
        text: noteText,
        menuId // Pass menuId if available for better cache invalidation
      },
      {
        onSuccess: () => {
          onClose(); // Close modal on successful save
        }
      }
    );
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    // Cleanup function to remove listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Helper to determine if we're displaying an optimistic update
  const isOptimisticUpdate = () => {
    return upsertNoteMutation.isPending;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 transition-opacity duration-300 ease-out backdrop-blur-sm" 
      aria-labelledby="notes-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={onClose} // Close if clicking the backdrop
    >
      <div 
        className="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all duration-300 ease-out scale-100"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
      >
        {/* Header */}
        <div className="border-b border-gray-200 pb-4 mb-4">
          <h3 
            id="notes-modal-title" 
            className="text-lg font-semibold leading-6 text-gray-900 flex justify-between items-center"
          >
            <span>{t('menu.editDayNote', 'Edit Note for Day')}</span>
            <button
              type="button"
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              onClick={onClose}
              aria-label={t('common.close', 'Close')}
            >
              <FontAwesomeIcon icon={faTimes} className="h-5 w-5" aria-hidden="true" />
            </button>
          </h3>
        </div>
        
        {/* Loading state */}
        {isNoteLoading && (
          <div className="flex items-center justify-center text-gray-500 py-4">
            <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin mr-2" />
            <span>{t('common.loading', 'Loading...')}</span>
          </div>
        )}
        
        {/* Note textarea */}
        <div className="mb-4">
          <label htmlFor="note-text" className="block text-sm font-medium text-gray-700 mb-1">
            {t('menu.noteText', 'Note Text')}
          </label>
          <textarea
            id="note-text"
            rows="4"
            className={`w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm transition-colors p-3 ${
              isOptimisticUpdate() ? 'bg-blue-50 border-blue-200' : ''
            }`}
            placeholder={t('menu.enterNoteHere', 'Enter note here...')}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            disabled={upsertNoteMutation.isPending}
          />
          {isOptimisticUpdate() && (
            <p className="mt-1 text-xs text-blue-500 italic">
              {t('common.optimisticUpdate', 'Preview (saving...)')}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {t('menu.noteForAllUsers', 'This note will be visible to all users with permission to view menus.')}
          </p>
        </div>

        {/* Error message */}
        {upsertNoteMutation.error && (
          <div className="my-4 p-3 text-sm text-red-600 bg-red-50 rounded-md border border-red-200">
            {upsertNoteMutation.error.message || t('errors.failedToSaveNote', 'Failed to save note. Please try again.')}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
            onClick={onClose}
            disabled={upsertNoteMutation.isPending}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-md border border-transparent text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
              upsertNoteMutation.isPending 
                ? 'bg-indigo-300 text-white cursor-not-allowed' 
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
            onClick={handleSave}
            disabled={upsertNoteMutation.isPending}
          >
            {upsertNoteMutation.isPending ? (
              <span className="flex items-center justify-center">
                <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
                {t('common.saving', 'Saving...')}
              </span>
            ) : t('common.saveNote', 'Save Note')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotesModal; 